import classNames from 'classnames';
import React, { FunctionComponent, useRef } from 'react';
import {
    Button,
    DayPicker,
    DayPickerProps,
    DayProps,
    useDayRender,
} from 'react-day-picker';

import { COLORS_JSON } from '@eventbrite/eds-color';
import {
    ArrowChevronLeft,
    ArrowChevronRight,
    ArrowLeftChunky,
    ArrowRightChunky,
} from '@eventbrite/eds-iconography';
import { gettext } from '@eventbrite/i18n';
import './DatePicker.scss';
import { formatWeekdayName } from './utils';

const BOOKED_DAY_CLASSNAME = 'booked-day';
const CONTROL_COLOR = COLORS_JSON['ui-blue--hover'];

const AvailableDayMark: FunctionComponent<{
    isSelected: boolean;
}> = ({ isSelected }) => (
    <svg
        data-testid="available-day-mark"
        version="1.1"
        xmlns="http://www.w3.org/2000/svg"
        className={classNames('date-picker__event_sales_mark', {
            'date-picker__event_sales_mark--selected': isSelected,
        })}
    >
        <circle r="2" cx="5" cy="5"></circle>
    </svg>
);

const DayWithAvailableMark: FunctionComponent<
    DayProps & { localeCode?: string }
> = ({ date, displayMonth, localeCode }) => {
    const buttonRef = useRef<HTMLButtonElement>(null);
    const { isButton, divProps, activeModifiers, buttonProps } = useDayRender(
        date,
        displayMonth,
        buttonRef,
    );

    if (!isButton) {
        return <div {...divProps} />;
    }

    const { isSelected, booked: dayIsAvailable } = activeModifiers;

    const localizedDate = date.toLocaleDateString(localeCode, {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });

    const { availableDayMark, accessibilityLabel } = dayIsAvailable
        ? {
              availableDayMark: <AvailableDayMark isSelected={isSelected} />,
              accessibilityLabel: gettext('%(localizedDate)s is available', {
                  localizedDate,
              }),
          }
        : {
              availableDayMark: null,
              accessibilityLabel: gettext(
                  '%(localizedDate)s is not available',
                  {
                      localizedDate,
                  },
              ),
          };

    return (
        <Button
            {...buttonProps}
            ref={buttonRef}
            aria-label={accessibilityLabel.toString()}
            role="button"
            aria-selected={isSelected}
        >
            {availableDayMark}
            {buttonProps.children}
        </Button>
    );
};

export const DatePicker: FunctionComponent<
    DayPickerProps & { isAppRebrandActive?: boolean; localeCode?: string }
> = ({
    components,
    isAppRebrandActive = false,
    modifiersClassNames,
    formatters,
    localeCode,
    ...props
}) => {
    const wrapperClass = isAppRebrandActive
        ? 'eds-date-picker--rebranding-active'
        : '';

    const IconRight: FunctionComponent = () => {
        return isAppRebrandActive ? (
            <ArrowChevronRight />
        ) : (
            <ArrowRightChunky fill={CONTROL_COLOR} />
        );
    };

    const IconLeft: FunctionComponent = () =>
        isAppRebrandActive ? (
            <ArrowChevronLeft />
        ) : (
            <ArrowLeftChunky fill={CONTROL_COLOR} />
        );

    return (
        <div className={wrapperClass}>
            <DayPicker
                {...props}
                components={{
                    ...components,
                    IconLeft,
                    IconRight,
                    Day: (props) => (
                        <DayWithAvailableMark
                            {...props}
                            localeCode={localeCode}
                        />
                    ),
                }}
                modifiersClassNames={{
                    ...modifiersClassNames,
                    booked: BOOKED_DAY_CLASSNAME,
                }}
                formatters={{
                    ...formatters,
                    formatWeekdayName,
                }}
            />
        </div>
    );
};
